import styles from '../BankPanels.module.css';
import { AceTable } from '../../../AceTable';
import { useApplicationStatusUtils } from '@axo/shared/data-access/hooks';
import { ITableConfig } from '../../../../models/tableConfig';
import { classNames } from '@axo/shared/util/dom';
import {
  loan_application,
  loan_quote,
  Product,
} from '@axo/shared/data-access/types';
import { Button, Icon } from '@blueprintjs/core';
import { Actions } from '../../Actions';
import { currencyFormatter } from '../../../../utils/formatCurrency';
import { LoanRequestStatus } from '../../LoanRequestStatus';
import { IncompleteApplication } from '../../IncompleteApplication/IncompleteApplication.tsx';
import { IQuoteDetail } from '../../hooks/useBankPanelData.tsx';
import { Reason } from '../../Reason';
import { IBankPanel } from '../IBankPanel.ts';
import { useGetLenderFilterResult } from '../../../../hooks/utils/useLenderFilterResult.tsx';
import { useLenderIsActiveWithSARR } from '../useLenderIsActiveWithSARR.tsx';
import { useApplicationDataStore } from '../../../../store/applicationDataStore.ts';
import { Bank } from '@axo/ui-content/features/Partners';
import type { BankCode } from '@axo/ui-content/features/Partners/Bank';

export function NotRegistered({ application, data, isLoading }: IBankPanel) {
  const applicationID = application.ID;
  const { isInAllApplicationStatusGroups } = useApplicationStatusUtils();
  const currencyFormat = currencyFormatter(application.Currency);
  const applicationIsIncomplete = application.Status
    ? isInAllApplicationStatusGroups(
        application.Status,
        loan_application.StatusGroup.Incomplete,
      )
    : true;
  const { getLenderFilterResult } = useGetLenderFilterResult(
    applicationID,
    data.map((quote) => quote.LoanRequestID),
  );
  const { lenderIsActiveWithSARR } = useLenderIsActiveWithSARR(applicationID);
  const { selectRequest, selectedLenderID } =
    useApplicationDataStore(applicationID);

  const getMostRecentlyActivatedQuote = (quotes: loan_quote.BankQuote[]) => {
    let active: loan_quote.BankQuote | undefined;
    quotes.forEach((quote) => {
      if (
        quote.ActivatedAt &&
        (!active?.ActivatedAt || quote.ActivatedAt > active.ActivatedAt)
      ) {
        active = quote;
      }
    });
    return active;
  };
  const activeQuote = getMostRecentlyActivatedQuote(data ?? []);

  function handleClickedRequestTag(lenderID: string, requestID: string) {
    selectRequest({ lenderID, requestID });
  }

  const bankPanelConfig: ITableConfig<IQuoteDetail> = {
    columns: {
      LenderID: {
        label: 'Bank',
        className: styles.lenderCell,
        render: (_, item, groupedStatuses) => (
          <div
            data-variant={
              item.Product !== Product.SecuredLoan
                ? item.Lender.BrokerConfiguration?.find(
                    (config) => config.Broker === application.Meta?.Broker,
                  )?.Tier
                : '0'
            }
            key={item.ID}
            className={classNames(
              groupedStatuses['LenderID'].isInGroup && styles.grouped,
              groupedStatuses['LenderID'].isFirstInGroup && styles.firstInGroup,
              groupedStatuses['LenderID'].isLastInGroup && styles.lastInGroup,
              styles.lender,
            )}
          >
            <Button
              minimal
              onClick={() =>
                handleClickedRequestTag(item.LenderID, item.LoanRequestID)
              }
              rightIcon={
                item.LenderID === activeQuote?.LenderID && (
                  <Icon icon="tick-circle" />
                )
              }
              className={styles.lenderButton}
            >
              <div className={styles.logoContainer}>
                <Bank
                  code={item.Lender.ShortName as BankCode}
                  name={item.Lender.Name}
                  country={application.MarketCountry}
                />
              </div>
            </Button>
          </div>
        ),
        width: 'medium',
        sticky: true,
      },
      LoanRequest: {
        label: 'Loan request status',
        render: (_, item, groupedStatuses) => (
          <LoanRequestStatus
            applicationID={applicationID}
            item={item}
            groupedStatuses={groupedStatuses}
            onClick={handleClickedRequestTag}
            lenderFilterResult={getLenderFilterResult(item.LoanRequestID)}
          />
        ),
        className: styles.loanRequests,
        shadow: 'right',
      },
      Amount: {
        label: 'Amount applied',
        render: (_, item) =>
          item.LoanRequest.Amount
            ? currencyFormat(item.LoanRequest.Amount.toString())
            : '-',
      },
      Duration: {
        label: 'Duration applied',
        render: (_, item) =>
          item.LoanRequest.Duration
            ? `${item.LoanRequest.Duration ?? 0}y`
            : '-',
      },
    },
    extra: [
      {
        label: 'Reason Not Registered',
        key: 'Reason',
        render: (quote) => (
          <Reason
            lenderStatus={quote.LenderStatus || ''}
            lenderFilterResult={getLenderFilterResult(quote.LoanRequestID)}
          />
        ),
      },
      {
        label: 'Created by',
        key: 'createdBy',
        render: (quote) => {
          return quote.LoanRequest.CreatedBy;
        },
      },
      {
        label: '',
        key: 'actions',
        render: (quote, groupedStatuses) => (
          <Actions
            application={application}
            quote={quote}
            data={data || []}
            groupedStatuses={groupedStatuses}
          />
        ),
        width: 'small',
        noTruncate: true,
        sticky: true,
        shadow: 'left',
      },
    ],
  };

  return (
    <>
      {applicationIsIncomplete ? (
        <IncompleteApplication application={application} />
      ) : (
        <AceTable<IQuoteDetail>
          config={bankPanelConfig}
          loading={isLoading}
          data={data}
          maxRows={10}
          uniqueRowIDKey="ID"
          groupingKeys={['LenderID', 'LoanRequestID', 'LoanQuoteID']}
          rowClassName={(row) =>
            classNames(
              row.LenderID === selectedLenderID && styles.selectedRow,
              row.ID === activeQuote?.ID && styles.activeRow,
              lenderIsActiveWithSARR(row.LenderID) && styles.sarrActive,
            )
          }
        />
      )}
    </>
  );
}
